<template>
	<div
		class="g-block-image-card"
		:class="{
			'g-block-image-card--edit': editable,
			'is-active': editable && active,
			'g-block-image-card--h5': h5,
		}"
		@click="onWrapClick"
	>
		<div class="g-block-image-card__list">
			<div
				v-for="item in items"
				:key="item.id"
				class="g-block-image-card__item"
			>
				<div
					class="g-block-image-card__cover"
					:class="{ 'is-previewable': isPreviewable }"
					:style="coverStyle"
					@click="onCoverClick(item, $event)"
				>
					<img
						v-if="getCoverUrl(item)"
						:src="getCoverUrl(item)"
						alt=""
					/>
					<i v-else class="mbm-iconfont mbm-tupian"></i>
					<span
						v-if="item.showImageCount && item.items.length"
						class="g-block-image-card__badge"
					>{{ item.items.length }}图</span>
				</div>
				<div
					class="g-block-image-card__subtitle"
					:style="{ color: item.subtitleColor }"
				>
					{{ item.subtitle }}
				</div>
				<div
					class="g-block-image-card__title"
					:style="{ color: item.titleColor }"
				>
					{{ item.title }}
				</div>
			</div>
		</div>

		<ImageGalleryLightbox
			v-model:visible="lightboxVisible"
			:urls="lightboxUrls"
			:initial-index="lightboxIndex"
		/>
	</div>
</template>

<script>
	import ImageGalleryLightbox from '../../../components/ImageGalleryLightbox.vue';
	import {
		getImageCardBlockStyle,
		getImageCardCoverImage,
		getImageCardImageUrl,
		normalizeImageCardData,
	} from '../../../utils/imageCard.js';

	export default {
		name: 'GImageCardShell',
		components: { ImageGalleryLightbox },
		props: {
			data: {
				type: Object,
				required: true,
			},
			editable: {
				type: Boolean,
				default: false,
			},
			active: {
				type: Boolean,
				default: false,
			},
			h5: {
				type: Boolean,
				default: false,
			},
		},
		emits: ['select'],
		data() {
			return {
				lightboxVisible: false,
				lightboxUrls: [],
				lightboxIndex: 0,
			};
		},
		computed: {
			normalizedData() {
				return normalizeImageCardData(this.data);
			},
			items() {
				return this.normalizedData.items;
			},
			blockStyle() {
				return getImageCardBlockStyle(this.data, { h5: this.h5 });
			},
			coverStyle() {
				return {
					aspectRatio: this.blockStyle.coverAspectRatio,
					borderRadius: this.blockStyle.borderRadius,
				};
			},
			isPreviewable() {
				return this.h5 && !this.editable;
			},
		},
		methods: {
			getImageUrl(image) {
				return getImageCardImageUrl(image);
			},
			getCoverUrl(item) {
				return getImageCardImageUrl(getImageCardCoverImage(item));
			},
			getItemGalleryUrls(item) {
				return (item.items || [])
					.map(image => this.getImageUrl(image))
					.filter(Boolean);
			},
			onWrapClick(e) {
				if (!this.editable) return;
				e?.stopPropagation?.();
				this.$emit('select');
			},
			onCoverClick(item, e) {
				if (!this.isPreviewable) return;
				e?.stopPropagation?.();
				const urls = this.getItemGalleryUrls(item);
				if (!urls.length) return;
				this.lightboxUrls = urls;
				this.lightboxIndex = 0;
				this.lightboxVisible = true;
			},
		},
	};
</script>

<style scoped lang="less">
	.g-block-image-card {
		width: 100%;
		box-sizing: border-box;

		&--edit {
			border: 1px solid transparent;
			border-radius: 4px;
			transition: border-color 0.2s, box-shadow 0.2s;
			cursor: pointer;

			&.is-active {
				border-color: #285af9;
				box-shadow: 0 0 0 1px rgba(40, 90, 249, 0.15);
			}
		}
	}

	.g-block-image-card__list {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 12px;
		width: 100%;

		.g-block-image-card--h5 & {
			gap: 0.24rem;
		}
	}

	.g-block-image-card__item {
		min-width: 0;
	}

	.g-block-image-card__cover {
		position: relative;
		width: 100%;
		height: auto;
		overflow: hidden;
		background: #f0f0f0;
		display: flex;
		align-items: center;
		justify-content: center;

		&.is-previewable {
			cursor: pointer;
		}

		img {
			width: 100%;
			height: auto;
			object-fit: cover;
			display: block;
		}

		i {
			font-size: 28px;
			color: #bfbfbf;
		}

		.g-block-image-card--h5 & i {
			font-size: 0.56rem;
		}
	}

	.g-block-image-card__badge {
		position: absolute;
		right: 8px;
		bottom: 8px;
		padding: 2px 8px;
		border-radius: 10px;
		background: rgba(0, 0, 0, 0.45);
		color: #fff;
		font-size: 11px;
		line-height: 1.4;
		pointer-events: none;

		.g-block-image-card--h5 & {
			right: 0.16rem;
			bottom: 0.16rem;
			padding: 0.04rem 0.16rem;
			border-radius: 0.2rem;
			font-size: 0.22rem;
		}
	}

	.g-block-image-card__subtitle {
		margin-top: 8px;
		font-size: 12px;
		line-height: 1.5;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;

		.g-block-image-card--h5 & {
			margin-top: 0.16rem;
			font-size: 0.24rem;
		}
	}

	.g-block-image-card__title {
		margin-top: 4px;
		font-size: 15px;
		font-weight: 600;
		line-height: 1.4;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;

		.g-block-image-card--h5 & {
			margin-top: 0.08rem;
			font-size: 0.3rem;
		}
	}
</style>
